// pages/HomePage.jsx
import React, {useState, useEffect, useRef, useCallback, useMemo} from "react";
import {NEURON_EVENTS} from "../config/eventTypes";
import TopStories from "../components/TopStories";
import PersonalisedSports from "../components/PersonalisedSports";
import PersonalisedCategories from "../components/PersonalisedCategories";
import FilterBar from "../components/FilterBar";
import LatestArticles from "../components/LatestArticles";
import {useStories} from "../hooks/useStories";
import {
  flattenNeuronRecommendationItems,
  getAutoNeuronRecommendations,
  NON_COMMENT_SCOPE,
} from "../utils/getNeuronRecommendations";
import {RECOMMENDATION_CONTEXTS} from "../config/recommendationContexts";
import SectionHeading from "../components/SectionHeading";
import StoryRail from "../components/StoryRail";
import {useDebugMode} from "../hooks/useDebugMode";
import PersonalisedRail from "../components/PersonalisedRail";
import PersonalisedFrontPage from "../components/PersonalisedFrontPage";
import {useNavigate} from "react-router-dom";
import {getNeuronItemId} from "../utils/getNeuronItemId";
import {trackNeuronEvent} from "../utils/trackNeuronEvent";

const HOVER_DELAY_MS = 2000;

/**
 * A single tile with delayed hover tracking (2s).
 * Logs only once per hover session (resets on mouse leave / blur).
 */
const HoverTrackedTile = React.forwardRef(
  ({story, userEmail, onClick, className, children}, ref) => {
    const hoverTimerRef = useRef(null);
    const hasLoggedThisHoverRef = useRef(false);

    const clearHoverTimer = useCallback(() => {
      if (hoverTimerRef.current) {
        window.clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    }, []);

    useEffect(() => {
      return () => clearHoverTimer();
    }, [clearHoverTimer]);

    const onHoverStart = useCallback(() => {
      if (!story || !userEmail) return;
      if (hasLoggedThisHoverRef.current) return;

      clearHoverTimer();

      hoverTimerRef.current = window.setTimeout(async () => {
        const neuronItemId = getNeuronItemId(story);
        if (!neuronItemId) {
          console.warn("Unable to determine Neuron item ID for story", story);
          return;
        }

        try {
          await trackNeuronEvent({
            itemId: neuronItemId,
            userId: userEmail,
            eventId: NEURON_EVENTS.hovered,
            // metadata: { surface: "homepage_tile" },
          });

          hasLoggedThisHoverRef.current = true;

          window.dispatchEvent(
            new CustomEvent("neuronEventLogged", {
              detail: {message: "Hover event logged.", status: "success"},
            })
          );
        } catch (err) {
          console.error("Error sending hover event:", err);
          window.dispatchEvent(
            new CustomEvent("neuronEventLogged", {
              detail: {message: "Failed to log hover.", status: "error"},
            })
          );
        } finally {
          hoverTimerRef.current = null;
        }
      }, HOVER_DELAY_MS);
    }, [story, userEmail, clearHoverTimer]);

    const onHoverEnd = useCallback(() => {
      clearHoverTimer();
      hasLoggedThisHoverRef.current = false;
    }, [clearHoverTimer]);

    return (
      <div
        ref={ref}
        className={className}
        onMouseEnter={onHoverStart}
        onMouseLeave={onHoverEnd}
        onFocus={onHoverStart}
        onBlur={onHoverEnd}
        onClick={onClick}
        role="button"
        tabIndex={0}
      >
        {children}
      </div>
    );
  }
);

HoverTrackedTile.displayName = "HoverTrackedTile";

/**
 * One auto-generated section.
 *
 * The engine names these itself from the reader's recent behaviour, so the
 * heading is machine-written - it is set in the paper's serif but introduced by
 * a blue-pencil kicker so a visitor can tell an editor did not write it. Uses
 * the same StoryRail as every other surface for the reasons in
 * .interface-design/system.md.
 */
const AutoSectionCarousel = ({section, data, isLoading, viewInterceptor}) => {
  const navigate = useNavigate();
  const debugMode = useDebugMode();

  const items = useMemo(
    () =>
      (data || [])
        .map((entry) => {
          const metadata = entry?.metadata;
          if (!metadata) return null;
          return {
            id: entry.id,
            title: metadata.originalTitle || entry.name || "Untitled",
            summary: metadata.summary || entry.description || "",
            image: metadata.image || "",
            category: metadata.aiCategorization || metadata.topic || "",
            meta: [metadata.source, metadata.timeToRead]
              .filter(Boolean)
              .join(" \u00b7 "),
            // base_score is similarity to the reader's profile. The final
            // `score` is that value after damping (impression fatigue can cut
            // it ~15x), which renders as a nonsensical "2% match".
            matchPercentage:
              typeof entry.baseScore === "number"
                ? Math.round(entry.baseScore * 100)
                : null,
            rank: entry.rank ?? null,
            explanation: entry.recommendationExplanation || null,
            source: entry.recommendationSource || "",
            story: {
              uuid: entry.id,
              originalTitle: metadata.originalTitle || entry.name,
              aiSummary: metadata.summary || entry.description,
              aiHtmlDescription: metadata.aiHtmlDescription,
              aiCategorization: metadata.aiCategorization || metadata.topic,
              image: metadata.image,
              source: metadata.source,
              timeToRead: metadata.timeToRead,
            },
          };
        })
        .filter(Boolean),
    [data]
  );

  const openStory = (item) => {
    if (!item?.id) return;
    try {
      sessionStorage.setItem(
        `neuron:article:${item.id}`,
        JSON.stringify(item.story)
      );
    } catch {
      // best effort
    }
    navigate(`/article/${item.id}`);
  };

  return (
    <StoryRail
      className="py-6"
      kicker="Written by the engine"
      title={section?.title || "More for you"}
      standfirst="Named and filled by the engine, from what you have read."
      debugNote={`auto · ${section?.section_id || "?"} · ${
        section?.reason?.type || "unknown"
      } · ${section?.reason?.window_days ?? "?"}d`}
      debugMode={debugMode}
      items={items}
      isLoading={isLoading}
      onSelect={openStory}
      getItemRef={(id) => viewInterceptor?.getItemRef(id)}
      emptyTitle="This section came back empty"
      emptyBody="The engine named a theme but found nothing new to fill it."
    />
  );
};

const HomePage = ({
  userEmail,
  hasCompletedOnboarding,
  devModeEnabled = false,
  personalisedData,
  loadingStates,
  fetchRecommendationsForContext,
  viewInterceptor, // now provided by App.jsx
}) => {
  const [selectedCategory, setSelectedCategory] = useState("");
  const {stories, loading: storiesLoading} = useStories();

  const categories = [
    ...new Set((stories || []).map((story) => story.aiCategorization)),
  ].filter(Boolean);

  // auto sections
  const [autoSections, setAutoSections] = useState([]); // [{ section, data }]
  // Deliberately not persisted. autoSections resets to [] on every mount, so a
  // cursor carried over from a previous visit would resume mid-stream against
  // an empty list - and once the stream had been exhausted, every later visit
  // would immediately report "done" and render nothing at all.
  const [autoCursor, setAutoCursor] = useState("");
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoDone, setAutoDone] = useState(false);

  const sentinelRef = useRef(null);
  const inFlightRef = useRef(false);

  const mapRecommendationsToTiles = useCallback((apiResponse) => {
    return flattenNeuronRecommendationItems(apiResponse).filter(
      (item) => item?.metadata?.type !== "comment"
    );
  }, []);

  const loadNextAutoSection = useCallback(async () => {
    if (!hasCompletedOnboarding || !userEmail) return;
    if (autoDone) return;
    if (inFlightRef.current) return;

    inFlightRef.current = true;
    setAutoLoading(true);

    try {
      const resp = await getAutoNeuronRecommendations({
        userId: userEmail,
        contextId: RECOMMENDATION_CONTEXTS.homepageForYou,
        limit: 20,
        cursor: autoCursor || undefined,
        scope: NON_COMMENT_SCOPE,
      });

      if (resp?.done || !resp?.section) {
        setAutoDone(true);
        return;
      }

      setAutoCursor(resp.next_cursor || "");

      const mapped = mapRecommendationsToTiles(resp);

      setAutoSections((prev) => [
        ...prev,
        {
          section: resp.section,
          data: mapped,
        },
      ]);
    } catch (err) {
      console.error("Error fetching auto section:", err);
    } finally {
      setAutoLoading(false);
      inFlightRef.current = false;
    }
  }, [
    hasCompletedOnboarding,
    userEmail,
    autoCursor,
    autoDone,
    mapRecommendationsToTiles,
  ]);

  useEffect(() => {
    if (!hasCompletedOnboarding || !userEmail) return;

    setAutoSections([]);
    setAutoDone(false);
    setAutoCursor("");

    void loadNextAutoSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCompletedOnboarding, userEmail]);

  useEffect(() => {
    if (!hasCompletedOnboarding || !userEmail) return;
    if (!sentinelRef.current) return;

    const el = sentinelRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries.some((e) => e.isIntersecting);
        if (!isIntersecting) return;
        void loadNextAutoSection();
      },
      {
        root: null,
        rootMargin: "600px",
        threshold: 0.01,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasCompletedOnboarding, userEmail, loadNextAutoSection]);

  return (
    <>
      <FilterBar
        categories={categories}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
      />

      {hasCompletedOnboarding ? (
        <>
          {/* The fold. Personalised before anything else - on a phone the old
              order meant scrolling past two control sections to reach it. */}
          <PersonalisedFrontPage
            contextId={RECOMMENDATION_CONTEXTS.homepageForYou}
            fetchRecommendationsForContext={fetchRecommendationsForContext}
            data={personalisedData[RECOMMENDATION_CONTEXTS.homepageForYou]}
            isLoading={loadingStates[RECOMMENDATION_CONTEXTS.homepageForYou]}
            viewInterceptor={viewInterceptor}
          />

          <PersonalisedSports
            contextId={RECOMMENDATION_CONTEXTS.general}
            fetchRecommendationsForContext={fetchRecommendationsForContext}
            data={personalisedData[RECOMMENDATION_CONTEXTS.general]}
            isLoading={loadingStates[RECOMMENDATION_CONTEXTS.general]}
            viewInterceptor={viewInterceptor}
            devModeEnabled={devModeEnabled}
          />

          <TopStories
            selectedCategory={selectedCategory}
            viewInterceptor={viewInterceptor}
          />

          {/* Editorial control on top of a ranked page. */}
          <PersonalisedRail
            contextId={RECOMMENDATION_CONTEXTS.editorsFrontPage}
            kicker="Editor's desk"
            title="The editor's front page"
            standfirst="A pinned lead and a boosted desk, sitting over the model's order."
            tone="editorial"
            debugNote="ctx 2326 · pin(topic=Politics → slot 1) · boost(topic=Politics ×2.2)"
            fetchRecommendationsForContext={fetchRecommendationsForContext}
            data={personalisedData[RECOMMENDATION_CONTEXTS.editorsFrontPage]}
            isLoading={loadingStates[RECOMMENDATION_CONTEXTS.editorsFrontPage]}
            viewInterceptor={viewInterceptor}
            emptyTitle="The editor has not set the page"
            emptyBody="No stories matched the pinned or boosted criteria on this run."
          />

          {/* Hard, auditable suppression. */}
          <PersonalisedRail
            contextId={RECOMMENDATION_CONTEXTS.familyEdition}
            kicker="Duty of care"
            title="The family edition"
            standfirst="Crime and violence withheld by a hard rule, applied before ranking."
            tone="editorial"
            debugNote="ctx 2327 · filter(topic in Crime, Public Safety, Military, Terrorism, Death, Accident) · drops ~30% of candidates"
            fetchRecommendationsForContext={fetchRecommendationsForContext}
            data={personalisedData[RECOMMENDATION_CONTEXTS.familyEdition]}
            isLoading={loadingStates[RECOMMENDATION_CONTEXTS.familyEdition]}
            viewInterceptor={viewInterceptor}
            emptyTitle="Nothing cleared for the family edition"
            emptyBody="Every candidate on this run matched the withheld categories."
          />

          {/* Suppression learned from behaviour rather than listed by hand. */}
          <PersonalisedRail
            contextId={RECOMMENDATION_CONTEXTS.tunedToYou}
            kicker="Learned, not listed"
            title="Tuned to you"
            standfirst="Damps the topics you skip. No editor wrote the list."
            debugNote="ctx 2328 · bury(user.topic_affinity.topic < 0.15 ×0.35) · weighted_topic(match ×1.6, 60d)"
            fetchRecommendationsForContext={fetchRecommendationsForContext}
            data={personalisedData[RECOMMENDATION_CONTEXTS.tunedToYou]}
            isLoading={loadingStates[RECOMMENDATION_CONTEXTS.tunedToYou]}
            viewInterceptor={viewInterceptor}
            emptyTitle="Not enough signal yet"
            emptyBody="Read a few stories and the engine will start damping what you skip."
          />

          {/* Personalisation arguing with itself. */}
          <PersonalisedRail
            contextId={RECOMMENDATION_CONTEXTS.secondOpinion}
            kicker="Against the grain"
            title="Second opinion"
            standfirst="Amplifies what you read least, so the paper stays wide."
            debugNote="ctx 2329 · weighted_topic(invert ×1.8, 60d) · diversity(topic max 2)"
            fetchRecommendationsForContext={fetchRecommendationsForContext}
            data={personalisedData[RECOMMENDATION_CONTEXTS.secondOpinion]}
            isLoading={loadingStates[RECOMMENDATION_CONTEXTS.secondOpinion]}
            viewInterceptor={viewInterceptor}
            emptyTitle="No counterweight yet"
            emptyBody="Once the engine knows what you favour, it can show you what you are missing."
          />

          <LatestArticles
            stories={stories}
            isLoading={storiesLoading}
            viewInterceptor={viewInterceptor}
            viewAllTo="/stories"
          />

          <PersonalisedCategories
            contextId={RECOMMENDATION_CONTEXTS.categories}
            fetchRecommendationsForContext={fetchRecommendationsForContext}
            data={personalisedData[RECOMMENDATION_CONTEXTS.categories]}
            isLoading={loadingStates[RECOMMENDATION_CONTEXTS.categories]}
            viewInterceptor={viewInterceptor}
            devModeEnabled={devModeEnabled}
          />

          {/* Auto sections rendered as the SAME carousel style */}
          <div className="container py-8">
            <SectionHeading
              kicker="Auto sections"
              title="More for you"
              standfirst="Themes the engine names and fills from your activity."
              action={
                autoDone ? (
                  <span className="font-sans text-meta text-ink-muted">
                    All caught up
                  </span>
                ) : autoLoading ? (
                  <span className="font-sans text-meta text-ink-muted">
                    Setting the next section...
                  </span>
                ) : null
              }
            />

            {autoSections.map((s) => (
              <AutoSectionCarousel
                key={s.section?.section_id}
                section={s.section}
                data={s.data}
                isLoading={false}
                userEmail={userEmail}
                viewInterceptor={viewInterceptor}
                devModeEnabled={devModeEnabled}
              />
            ))}

            {/* Sections load themselves as the reader reaches the bottom. */}
            <div ref={sentinelRef} className="h-10" />
          </div>
        </>
      ) : (
        <div className="container py-16">
          <div className="border-t-2 border-ink pt-6 text-center">
            <p className="font-serif text-h2 font-bold text-ink">
              Your edition is not set yet
            </p>
            <p className="mx-auto mt-2 max-w-md text-body text-ink-secondary">
              Finish the short intro and the personalised sections below will be
              ranked for you, live, on every visit.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default HomePage;
